// Product reviews — write-review disclosure + app-proxy submit + helpful votes.
// Same-origin storefront calls go through the Shopify App Proxy under /apps/pttek.
const REVIEWS_SECTION_SELECTOR = '[data-reviews-section]';
const APP_PROXY_BASE = '/apps/pttek';
const REQUEST_TIMEOUT_MS = 10000;
const PURCHASE_CHECK_UNAVAILABLE_MESSAGE = 'Chưa kiểm tra được đơn hàng. Vui lòng thử lại sau.';
const reviewStates = new Map();

const ready = (callback) => {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback, { once: true });
  else callback();
};
const sectionKey = (section) => section.dataset.reviewsSectionId || section.id || 'reviews-section';
const listen = (state, target, type, handler, options) => {
  target.addEventListener(type, handler, options);
  state.cleanups.push(() => target.removeEventListener(type, handler, options));
};

function reviewSubmissionMessage(error, fallback) {
  return error && typeof error.serverMessage === 'string' && error.serverMessage.trim()
    ? error.serverMessage
    : fallback;
}

const postJson = async (state, path, payload) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  state.controllers.add(controller);
  try {
    const response = await fetch(`${APP_PROXY_BASE}${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      signal: controller.signal,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      const error = new Error(`Review request failed: ${response.status}`);
      error.status = response.status;
      if (typeof payload.message === 'string') error.serverMessage = payload.message;
      throw error;
    }
    return response.json().catch(() => ({}));
  } finally {
    window.clearTimeout(timeoutId);
    state.controllers.delete(controller);
  }
};

const setStatus = (statusNode, message, tone) => {
  if (!statusNode) return;
  statusNode.textContent = message || '';
  statusNode.dataset.tone = tone || '';
  statusNode.hidden = !message;
};

const initWriteForm = (section, state) => {
  const toggle = section.querySelector('[data-review-toggle]');
  const form = section.querySelector('[data-review-form]');
  if (!toggle || !form) return;
  const status = form.querySelector('[data-review-status]');
  const submit = form.querySelector('[data-review-submit]');

  listen(state, toggle, 'click', () => {
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    form.hidden = expanded;
    if (!expanded) {
      const firstField = form.querySelector('input[name="rating"]');
      if (firstField) firstField.focus();
    }
  });

  listen(state, form, 'submit', async (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const rating = Number(data.get('rating') || 0);
    const body = String(data.get('body') || '').trim();
    if (!rating || !body) {
      setStatus(status, form.dataset.msgRequired, 'error');
      return;
    }
    const payload = {
      product_id: data.get('product_id'),
      product_handle: data.get('product_handle'),
      rating,
      title: String(data.get('title') || '').trim(),
      body,
      author_name: String(data.get('author_name') || '').trim(),
      email: String(data.get('email') || '').trim()
    };
    if (submit) submit.disabled = true;
    setStatus(status, '', '');
    try {
      await postJson(state, '/reviews', payload);
      setStatus(status, form.dataset.msgSuccess, 'success');
      form.reset();
    } catch (error) {
      if (error.name === 'AbortError') {
        setStatus(status, PURCHASE_CHECK_UNAVAILABLE_MESSAGE, 'error');
        return;
      }
      console.error('Review could not be submitted', error);
      setStatus(status, reviewSubmissionMessage(error, form.dataset.msgError), 'error');
    } finally {
      if (submit) submit.disabled = false;
    }
  });
};

const initHelpful = (section, state) => {
  listen(state, section, 'click', async (event) => {
    const button = event.target.closest('[data-review-helpful]');
    if (!button || !section.contains(button) || button.disabled) return;
    const label = button.querySelector('[data-helpful-label]');
    const template = button.dataset.helpfulTemplate || '';
    const next = Number(button.dataset.helpfulCount || 0) + 1;
    button.dataset.helpfulCount = String(next);
    if (label && template) label.textContent = template.replace('__COUNT__', next);
    button.disabled = true;
    const reviewId = button.dataset.reviewId;
    if (!reviewId) return;
    try {
      await postJson(state, '/reviews/helpful', { review_id: reviewId });
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Helpful vote could not be recorded', error);
    }
  });
};

function destroyReviewsSection(section) {
  const state = reviewStates.get(sectionKey(section));
  if (!state) return;
  state.controllers.forEach((controller) => controller.abort());
  state.controllers.clear();
  state.cleanups.forEach((cleanup) => cleanup());
  reviewStates.delete(sectionKey(section));
}
const initReviewsSection = (section) => {
  destroyReviewsSection(section);
  const state = { cleanups: [], controllers: new Set() };
  reviewStates.set(sectionKey(section), state);
  initWriteForm(section, state);
  initHelpful(section, state);
};
const sectionsFrom = (root) => {
  const sections = [];
  if (root instanceof Element && root.matches(REVIEWS_SECTION_SELECTOR)) sections.push(root);
  root.querySelectorAll?.(REVIEWS_SECTION_SELECTOR).forEach((section) => sections.push(section));
  return sections;
};

ready(() => sectionsFrom(document).forEach(initReviewsSection));
document.addEventListener('shopify:section:load', (event) => sectionsFrom(event.target).forEach(initReviewsSection));
document.addEventListener('shopify:section:unload', (event) => sectionsFrom(event.target).forEach(destroyReviewsSection));
